import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { track } from '@/modules/Analytics';
import { CompanyAccountCategory } from '@/types/types';

export type PartnerStatus = 'shadow' | 'invited' | 'claimed' | 'requested';

export type Partner = {
  id: number;
  company_id: number;
  name: string;
  status: PartnerStatus;
  invite_email?: string | null;
  invite_token_expires_at?: string | null;
  company_account_category_id?: string;
  contact_phone?: string;
  contact_email?: string;
  hourly_rate?: number;
  contact_name?: string;
  company_account_category?: CompanyAccountCategory;
  projects_count: number;
  last_used_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type CreatePartnerParams = {
  name: string;
  company_account_category_id?: string;
  contact_phone?: string;
  contact_email?: string;
  hourly_rate?: number;
  contact_name?: string;
};

export type UpdatePartnerParams = {
  name?: string;
  company_account_category_id?: string;
  contact_phone?: string;
  contact_email?: string;
  hourly_rate?: number;
  contact_name?: string;
};

export const usePartners = () => {
  const { axiosInstance } = useApi();
  return useQuery<Partner[]>({
    queryKey: queryKeys.partners.all.queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get('/api/v1/partners');
      return data;
    },
  });
};

export const usePartner = (
  id: number | string,
  options?: Omit<UseQueryOptions<Partner>, 'queryKey' | 'queryFn'>
) => {
  const { axiosInstance } = useApi();
  return useQuery<Partner>({
    queryKey: queryKeys.partners.detail(id).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/api/v1/partners/${id}`);
      return data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCreatePartner = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreatePartnerParams) => {
      const { data } = await axiosInstance.post('/api/v1/partners', params);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners._def });
    },
  });
};

export const useUpdatePartner = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, params }: { id: number | string; params: UpdatePartnerParams }) => {
      const { data } = await axiosInstance.put(`/api/v1/partners/${id}`, params);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.partners.detail(data.id).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.partners._def });
    },
  });
};

export const useDeletePartner = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      await axiosInstance.delete(`/api/v1/partners/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners._def });
    },
  });
};

export type ClaimPartnerParams = {
  token: string;
  company_name: string;
  name: string;
  email: string;
  password: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
};

export const useClaimPartner = () => {
  const { axiosInstance } = useApi();
  return useMutation({
    mutationFn: async (params: ClaimPartnerParams) => {
      const { data } = await axiosInstance.post('/api/v1/partners/claim', params);
      return data;
    },
  });
};

export type PartnerClaimInfo = {
  company_name: string;
  email: string;
};

export const usePartnerClaimInfo = (token: string) => {
  const { axiosInstance } = useApi();
  return useQuery<PartnerClaimInfo>({
    queryKey: queryKeys.partners.claimInfo(token).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/api/v1/public/partner_claims/${token}`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });
};

export type LinkPartnerParams = {
  token: string;
  force?: boolean;
};

export const useLinkPartner = () => {
  const { axiosInstance } = useApi();
  return useMutation({
    mutationFn: async (params: LinkPartnerParams) => {
      const { data } = await axiosInstance.post('/api/v1/partners/link', params);
      return data;
    },
  });
};

export const useCancelInvitePartner = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (partnerId: number | string) => {
      const { data } = await axiosInstance.delete(`/api/v1/partners/${partnerId}/invite`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners._def });
    },
  });
};

export const useInvitePartner = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ partnerId, email }: { partnerId: number | string; email?: string }) => {
      const { data } = await axiosInstance.post(`/api/v1/partners/${partnerId}/invite`, { email });
      return data;
    },
    onSuccess: () => {
      track('partner_invited');
      queryClient.invalidateQueries({ queryKey: queryKeys.partners._def });
    },
  });
};

export const useApprovePartnerRequest = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (partnerId: number | string) => {
      const { data } = await axiosInstance.post(`/api/v1/partners/${partnerId}/approve_request`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners._def });
    },
  });
};

export const useDeclinePartnerRequest = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (partnerId: number | string) => {
      const { data } = await axiosInstance.delete(`/api/v1/partners/${partnerId}/approve_request`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners._def });
    },
  });
};
